import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DEFAULT_DOCUMENT_FILTERS,
  type BagelDocumentFilterState,
} from '../components/DocumentsTab/documentTableFilters';
import {
  bagelDocumentsTableNeedsUrlDefaults,
  bagelDocumentsTableStateToParams,
  buildDefaultBagelDocumentsTableParams,
  parseBagelDocumentsTableUrlState,
} from '../utils/bagelDocumentsTableSearchParams.util';

export default function useBagelDocumentsTableSearchParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const tableState = useMemo(
    () => parseBagelDocumentsTableUrlState(searchParams),
    [searchParams],
  );

  useEffect(() => {
    if (bagelDocumentsTableNeedsUrlDefaults(searchParams)) {
      setSearchParams(
        buildDefaultBagelDocumentsTableParams(searchParams),
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  const applyTableState = useCallback(
    (
      next: Partial<typeof tableState> & {
        filters?: Partial<BagelDocumentFilterState>;
      },
    ) => {
      setSearchParams(
        prev =>
          bagelDocumentsTableStateToParams(
            {
              ...tableState,
              ...next,
              filters: {
                ...tableState.filters,
                ...next.filters,
              },
            },
            prev,
          ),
        { replace: true },
      );
    },
    [setSearchParams, tableState],
  );

  const onPageChange = useCallback(
    (page: number) => {
      applyTableState({ page });
    },
    [applyTableState],
  );

  const onChangeLimit = useCallback(
    (limit: number) => {
      applyTableState({ page: 1, limit });
    },
    [applyTableState],
  );

  const onSortColumn = useCallback(
    (sortColumn: string, sortType: 'asc' | 'desc') => {
      applyTableState({ sortColumn, sortType });
    },
    [applyTableState],
  );

  const onSearch = useCallback(
    (search: string) => {
      applyTableState({ page: 1, search });
    },
    [applyTableState],
  );

  const debouncedSearch = useMemo(
    () => debounce(onSearch, 300),
    [onSearch],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const setFilter = useCallback(
    <K extends keyof BagelDocumentFilterState>(
      key: K,
      value: BagelDocumentFilterState[K],
    ) => {
      applyTableState({
        page: 1,
        filters: {
          ...tableState.filters,
          [key]: value,
        },
      });
    },
    [applyTableState, tableState.filters],
  );

  const clearAllFilters = useCallback(() => {
    applyTableState({
      page: 1,
      search: '',
      filters: DEFAULT_DOCUMENT_FILTERS,
    });
  }, [applyTableState]);

  return {
    page: tableState.page,
    limit: tableState.limit,
    sortColumn: tableState.sortColumn,
    sortType: tableState.sortType,
    search: tableState.search,
    filters: tableState.filters,
    onPageChange,
    onChangeLimit,
    onSortColumn,
    onSearch,
    debouncedSearch,
    setFilter,
    clearAllFilters,
  };
}
